import { useState } from "react";
import type { JobPhoto } from "../tabs/PhotosTab";

interface Props {
  photo: JobPhoto;
  index: number;
  total: number;
  onToggleSelected: (p: JobPhoto) => void;
  onToggleFavorite: (p: JobPhoto) => void;
  onDelete: (p: JobPhoto) => void;
  onMoveUp: (p: JobPhoto) => void;
  onMoveDown: (p: JobPhoto) => void;
  onSaveEdit: (p: JobPhoto, filename: string, category: string) => void;
  disabled: boolean;
  mode?: "grid" | "note";
}

export default function PhotoCard({
  photo,
  index,
  total,
  onToggleSelected,
  onToggleFavorite,
  onDelete,
  onMoveUp,
  onMoveDown,
  onSaveEdit,
  disabled,
  mode = "grid",
}: Props) {
  const [imgError, setImgError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editFilename, setEditFilename] = useState(photo.filename ?? "");
  const [editCategory, setEditCategory] = useState(photo.category ?? "");

  const handleSave = () => {
    onSaveEdit(photo, editFilename, editCategory);
    setEditing(false);
  };

  /* ── Note mode ─────────────────────────────────────────────── */
  if (mode === "note") {
    return (
      <div className="flex gap-4 bg-white border border-slate-200 rounded-xl p-4 items-start hover:shadow-sm transition-shadow">
        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
          {imgError ? (
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              <i className="ri-image-line text-lg"></i>
            </div>
          ) : (
            <img
              src={photo.thumbnail_url ?? photo.photo_url}
              alt={photo.filename ?? "Photo"}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            {photo.is_selected && (
              <span className="bg-[#C8A96E] text-black text-[10px] font-bold px-2 py-0.5 rounded-full">
                Selected
              </span>
            )}
            {photo.is_favorite && (
              <span className="bg-red-400/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <i className="ri-heart-fill text-[10px]"></i>
                Fav
              </span>
            )}
            {photo.category && (
              <span className="bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">
                {photo.category}
              </span>
            )}
          </div>
          {photo.filename && (
            <p className="text-slate-700 text-sm font-medium truncate">{photo.filename}</p>
          )}
          <p className="text-slate-600 text-sm leading-relaxed">{photo.selection_note}</p>
        </div>
      </div>
    );
  }

  /* ── Grid mode ─────────────────────────────────────────────── */
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden group transition-shadow hover:shadow-sm">
      {/* Image */}
      <div className="aspect-square bg-slate-100 overflow-hidden relative">
        {imgError ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-400">
            <span className="w-8 h-8 flex items-center justify-center">
              <i className="ri-image-line text-2xl"></i>
            </span>
            <p className="text-xs text-center px-2 break-all line-clamp-3">{photo.photo_url}</p>
          </div>
        ) : (
          <img
            src={photo.thumbnail_url ?? photo.photo_url}
            alt={photo.filename ?? "Photo"}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={() => setImgError(true)}
          />
        )}

        {/* Badges */}
        <div className="absolute top-2 right-2 flex flex-col gap-1.5">
          {photo.is_selected && (
            <span className="bg-[#C8A96E] text-black text-[10px] font-bold px-2 py-0.5 rounded-full">
              Selected
            </span>
          )}
          {photo.is_favorite && (
            <span className="bg-red-400/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
              <i className="ri-heart-fill text-[10px]"></i>
              Fav
            </span>
          )}
        </div>

        {!editing && photo.category && (
          <div className="absolute bottom-2 left-2">
            <span className="bg-black/60 text-white/80 text-[10px] px-2 py-0.5 rounded-full">
              {photo.category}
            </span>
          </div>
        )}
      </div>

      {/* Info + actions */}
      <div className="p-3 space-y-2">
        {editing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editFilename}
              onChange={(e) => setEditFilename(e.target.value)}
              placeholder="Filename"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
            <input
              type="text"
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
              placeholder="Category"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleSave}
                disabled={disabled}
                className="flex-1 flex items-center justify-center gap-1 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-[11px] font-semibold py-1.5 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-check-line"></i> Save
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setEditFilename(photo.filename ?? "");
                  setEditCategory(photo.category ?? "");
                }}
                className="flex-1 flex items-center justify-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-500 text-[11px] font-medium py-1.5 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {photo.filename && (
              <p className="text-slate-500 text-xs truncate">{photo.filename}</p>
            )}
            <div className="flex items-center gap-1 justify-end">
              <button
                onClick={() => onMoveUp(photo)}
                disabled={disabled || index === 0}
                title="Move up"
                className="w-7 h-7 flex items-center justify-center rounded-lg text-xs text-slate-400 hover:text-[#C8A96E] hover:bg-amber-50 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-up-line"></i>
              </button>
              <button
                onClick={() => onMoveDown(photo)}
                disabled={disabled || index === total - 1}
                title="Move down"
                className="w-7 h-7 flex items-center justify-center rounded-lg text-xs text-slate-400 hover:text-[#C8A96E] hover:bg-amber-50 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-down-line"></i>
              </button>
              <button
                onClick={() => setEditing(true)}
                disabled={disabled}
                title="Edit filename / category"
                className="w-7 h-7 flex items-center justify-center rounded-lg text-xs text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all cursor-pointer disabled:opacity-40"
              >
                <i className="ri-pencil-line"></i>
              </button>
              <button
                onClick={() => onToggleSelected(photo)}
                disabled={disabled}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs transition-all cursor-pointer disabled:opacity-40 ${
                  photo.is_selected
                    ? "bg-[#C8A96E]/15 text-[#C8A96E]"
                    : "text-slate-400 hover:text-[#C8A96E] hover:bg-amber-50"
                }`}
              >
                <i
                  className={
                    photo.is_selected ? "ri-checkbox-circle-fill" : "ri-checkbox-circle-line"
                  }
                ></i>
              </button>
              <button
                onClick={() => onToggleFavorite(photo)}
                disabled={disabled}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs transition-all cursor-pointer disabled:opacity-40 ${
                  photo.is_favorite
                    ? "bg-red-400/15 text-red-400"
                    : "text-slate-400 hover:text-red-400 hover:bg-red-50"
                }`}
              >
                <i className={photo.is_favorite ? "ri-heart-fill" : "ri-heart-line"}></i>
              </button>
              <button
                onClick={() => onDelete(photo)}
                disabled={disabled}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-xs text-slate-400 hover:text-red-400 hover:bg-red-50 transition-all cursor-pointer disabled:opacity-40"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}